/**
 * Phase 5.9.15 — Contact / Support form.
 *
 * Public-facing form (works for both anonymous visitors and authed users).
 * Submission creates a ticket that lands in the dispatcher Inbox tab.
 * Authed users get their name/email/region pre-filled.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, MessageSquare, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { ContactPageModern } from './ContactPageModern';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export default function ContactPage() {
  const { user } = useAuth();
  const { isModern } = useTheme();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [ticketId, setTicketId] = useState('');
  const [error, setError] = useState('');

  // Pre-fill from authenticated user
  useEffect(() => {
    if (user && user.id) {
      setName(user.name || '');
      setEmail(user.email || '');
      setPhone(user.phone || '');
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim() || !subject.trim() || !body.trim()) {
      setError('Please fill out name, email, subject, and message.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        subject: subject.trim(),
        body: body.trim(),
      };
      if (phone.trim()) payload.phone = phone.trim();
      if (user?.region) payload.region = user.region;
      const { data } = await axios.post(`${API_URL}/api/contact`, payload, { withCredentials: true });
      setTicketId(data.id || '');
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not send your message. Please try again or email support@ashandmerch.com.');
    } finally {
      setLoading(false);
    }
  };

  if (isModern) {
    const resetForm = () => { setSent(false); setSubject(''); setBody(''); setTicketId(''); };
    const f = { name, setName, email, setEmail, phone, setPhone, subject, setSubject, body, setBody, loading, sent, ticketId, error, handleSubmit, resetForm };
    return <ContactPageModern f={f} isCustomer={user?.role === 'customer'} />;
  }

  return (
    <div className="min-h-screen bg-[#050003] p-4 flex items-center justify-center" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 16px)' }}>
      <div className="w-full max-w-lg bg-[#0C0612] border border-[#2A0F3D] p-6 rounded" data-testid="contact-page">
        <h1 className="text-[#39FF14] font-mono text-sm mb-1">{'>'} ASH&MERCH_</h1>
        <h2 className="text-white text-2xl font-bold mb-2 flex items-center gap-2" data-testid="contact-page-title">
          <MessageSquare className="w-6 h-6 text-[#39FF14]" />
          Contact dispatch
        </h2>
        <p className="text-[#A3C7A8] text-sm mb-6 font-mono">// We usually respond within an hour during operating hours.</p>

        {sent ? (
          <div className="space-y-4" data-testid="contact-sent">
            <div className="flex items-start gap-3 p-4 border border-[#39FF14] bg-[rgba(57,255,20,0.06)] rounded">
              <CheckCircle2 className="w-6 h-6 text-[#39FF14] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-mono text-sm font-bold">// MESSAGE_RECEIVED</p>
                <p className="text-[#A3C7A8] text-sm mt-1">
                  Thanks {name.split(' ')[0]}. A dispatcher will reach out to <span className="text-white">{email}</span>{phone ? <> or <span className="text-white">{phone}</span></> : null}.
                </p>
                {ticketId && (
                  <p className="text-[#6B8E73] text-xs font-mono mt-2">Reference ID: #{ticketId.slice(-8).toUpperCase()}</p>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                onClick={() => { setSent(false); setSubject(''); setBody(''); setTicketId(''); }}
                variant="outline"
                className="border-[#2A0F3D] text-[#A3C7A8] font-mono rounded-none"
                data-testid="contact-send-another"
              >
                Send another
              </Button>
              <Link
                to={user?.role === 'customer' ? '/customer' : '/'}
                className="inline-flex items-center justify-center px-4 py-2 bg-[#39FF14] text-[#050003] font-mono font-bold rounded text-sm"
                data-testid="contact-back-home"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> {user?.role === 'customer' ? 'Back to shop' : 'Back to home'}
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
            <div>
              <label className="text-[#A3C7A8] text-xs font-mono uppercase tracking-wider block mb-1">Name *</label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={120}
                disabled={loading}
                className="bg-black border-[#2A0F3D] text-white font-mono rounded-none"
                data-testid="contact-input-name"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[#A3C7A8] text-xs font-mono uppercase tracking-wider block mb-1">Email *</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="bg-black border-[#2A0F3D] text-white font-mono rounded-none"
                  data-testid="contact-input-email"
                />
              </div>
              <div>
                <label className="text-[#A3C7A8] text-xs font-mono uppercase tracking-wider block mb-1">Phone</label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  maxLength={40}
                  disabled={loading}
                  className="bg-black border-[#2A0F3D] text-white font-mono rounded-none"
                  data-testid="contact-input-phone"
                />
              </div>
            </div>
            <div>
              <label className="text-[#A3C7A8] text-xs font-mono uppercase tracking-wider block mb-1">Subject *</label>
              <Input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                maxLength={200}
                disabled={loading}
                placeholder="Order issue / Product question / Feedback…"
                className="bg-black border-[#2A0F3D] text-white font-mono rounded-none"
                data-testid="contact-input-subject"
              />
            </div>
            <div>
              <label className="text-[#A3C7A8] text-xs font-mono uppercase tracking-wider block mb-1">Message *</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
                maxLength={5000}
                rows={6}
                disabled={loading}
                placeholder="Tell us what's going on. Include an order number if applicable."
                className="w-full bg-black border border-[#2A0F3D] text-white font-mono p-3 rounded-none text-sm resize-y"
                data-testid="contact-input-body"
              />
              <div className="text-right text-[10px] text-[#6B8E73] font-mono mt-1">{body.length}/5000</div>
            </div>
            {error && (
              <div className="text-[#FF3B30] text-xs font-mono p-2 border border-[#FF3B30] bg-[rgba(255,59,48,0.06)] rounded" data-testid="contact-error">
                {error}
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#39FF14] hover:bg-[#4AF626] text-[#050003] font-mono font-bold rounded-none uppercase tracking-wider h-11"
                data-testid="contact-submit"
              >
                {loading ? '// SENDING...' : '> SEND MESSAGE'}
              </Button>
              <Link
                to={user?.role === 'customer' ? '/customer' : '/'}
                className="inline-flex items-center justify-center px-4 py-2 border border-[#2A0F3D] text-[#A3C7A8] font-mono text-sm hover:border-[#39FF14] hover:text-[#39FF14] rounded-none h-11"
                data-testid="contact-cancel"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Cancel
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
